/**
 * OrgLinks Page
 * Manage organization links and personal "My Links" for sites
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Pencil, Trash2, ExternalLink, User, Building2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { fieldCls, primaryBtnCls, secondaryBtnCls } from '../../ui';

interface Site {
  id: string;
  name: string;
  code: string;
}

interface OrgLink {
  id: string;
  site_id: string;
  name: string;
  url: string;
  description: string | null;
  icon_url: string | null;
  display_order: number;
  is_active: boolean;
}

interface MyLink {
  id: string;
  name: string;
  url: string;
  description: string | null;
  icon_url: string | null;
  display_order: number;
}

interface LinkFormData {
  name: string;
  url: string;
  description: string;
  icon_url: string;
}

const emptyFormData: LinkFormData = {
  name: '',
  url: '',
  description: '',
  icon_url: '',
};

export function OrgLinks() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'super_admin' || user?.role === 'administrator';

  const [sites, setSites] = useState<Site[]>([]);
  const [selectedSiteId, setSelectedSiteId] = useState<string>('');
  const [orgLinks, setOrgLinks] = useState<OrgLink[]>([]);
  const [myLinks, setMyLinks] = useState<MyLink[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [showOrgForm, setShowOrgForm] = useState(false);
  const [showMyForm, setShowMyForm] = useState(false);
  const [editingOrgId, setEditingOrgId] = useState<string | null>(null);
  const [editingMyId, setEditingMyId] = useState<string | null>(null);
  const [formData, setFormData] = useState<LinkFormData>(emptyFormData);
  const [saving, setSaving] = useState(false);

  // Fetch sites
  useEffect(() => {
    const fetchSites = async () => {
      try {
        const response = await api.get('/api/sites');
        setSites(response.data);
        if (response.data.length === 1) {
          setSelectedSiteId(response.data[0].id);
        }
      } catch (err) {
        console.error('Error fetching sites:', err);
      }
    };
    fetchSites();
  }, []);

  // Fetch links when site changes
  useEffect(() => {
    if (!selectedSiteId) {
      setOrgLinks([]);
      setMyLinks([]);
      return;
    }

    const fetchLinks = async () => {
      setLoading(true);
      try {
        // Fetch org links
        const orgEndpoint = isAdmin
          ? `/api/sites/${selectedSiteId}/links/all`
          : `/api/sites/${selectedSiteId}/links`;
        const orgResponse = await api.get(orgEndpoint);
        setOrgLinks(orgResponse.data);

        // Fetch my links (personal links from the merged endpoint)
        const myResponse = await api.get('/api/my-quick-links', {
          headers: { 'X-Site-Id': selectedSiteId }
        });
        // Filter to only personal links
        const personalLinks = myResponse.data.links.filter((l: any) => l.type === 'personal');
        setMyLinks(personalLinks);
      } catch (err) {
        console.error('Error fetching links:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchLinks();
  }, [selectedSiteId, isAdmin]);

  // Org Link handlers
  const handleAddOrg = () => {
    setEditingOrgId(null);
    setFormData(emptyFormData);
    setShowOrgForm(true);
    setShowMyForm(false);
    setError(null);
  };

  const handleEditOrg = (link: OrgLink) => {
    setEditingOrgId(link.id);
    setFormData({
      name: link.name,
      url: link.url,
      description: link.description || '',
      icon_url: link.icon_url || '',
    });
    setShowOrgForm(true);
    setShowMyForm(false);
    setError(null);
  };

  const handleSubmitOrg = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.url) return;

    setSaving(true);
    setError(null);

    try {
      if (editingOrgId) {
        const response = await api.patch(`/api/sites/${selectedSiteId}/links/${editingOrgId}`, {
          name: formData.name,
          url: formData.url,
          description: formData.description || null,
          icon_url: formData.icon_url || null,
        });
        setOrgLinks(prev => prev.map(l => l.id === editingOrgId ? response.data : l));
      } else {
        const response = await api.post(`/api/sites/${selectedSiteId}/links`, {
          name: formData.name,
          url: formData.url,
          description: formData.description || null,
          icon_url: formData.icon_url || null,
          display_order: orgLinks.length,
        });
        setOrgLinks(prev => [...prev, response.data]);
      }
      handleCancelForm();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save link');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteOrg = async (linkId: string) => {
    if (!window.confirm('Delete this organization link?')) return;

    try {
      await api.delete(`/api/sites/${selectedSiteId}/links/${linkId}`);
      setOrgLinks(prev => prev.filter(l => l.id !== linkId));
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete link');
    }
  };

  const handleToggleActive = async (link: OrgLink) => {
    try {
      const response = await api.patch(`/api/sites/${selectedSiteId}/links/${link.id}`, {
        is_active: !link.is_active,
      });
      setOrgLinks(prev => prev.map(l => l.id === link.id ? response.data : l));
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update link');
    }
  };

  // My Link handlers
  const handleAddMy = () => {
    setEditingMyId(null);
    setFormData(emptyFormData);
    setShowMyForm(true);
    setShowOrgForm(false);
    setError(null);
  };

  const handleEditMy = (link: MyLink) => {
    setEditingMyId(link.id);
    setFormData({
      name: link.name,
      url: link.url,
      description: link.description || '',
      icon_url: link.icon_url || '',
    });
    setShowMyForm(true);
    setShowOrgForm(false);
    setError(null);
  };

  const handleSubmitMy = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.url) return;

    setSaving(true);
    setError(null);

    try {
      if (editingMyId) {
        const response = await api.patch(`/api/my-quick-links/${editingMyId}`, {
          name: formData.name,
          url: formData.url,
          description: formData.description || null,
          icon_url: formData.icon_url || null,
        }, {
          headers: { 'X-Site-Id': selectedSiteId }
        });
        setMyLinks(prev => prev.map(l => l.id === editingMyId ? { ...response.data, type: 'personal' } : l));
      } else {
        const response = await api.post('/api/my-quick-links', {
          name: formData.name,
          url: formData.url,
          description: formData.description || null,
          icon_url: formData.icon_url || null,
          display_order: myLinks.length,
        }, {
          headers: { 'X-Site-Id': selectedSiteId }
        });
        setMyLinks(prev => [...prev, { ...response.data, type: 'personal' }]);
      }
      handleCancelForm();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save link');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteMy = async (linkId: string) => {
    if (!window.confirm('Delete this personal link?')) return;

    try {
      await api.delete(`/api/my-quick-links/${linkId}`, {
        headers: { 'X-Site-Id': selectedSiteId }
      });
      setMyLinks(prev => prev.filter(l => l.id !== linkId));
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete link');
    }
  };

  const handleCancelForm = () => {
    setShowOrgForm(false);
    setShowMyForm(false);
    setEditingOrgId(null);
    setEditingMyId(null);
    setFormData(emptyFormData);
    setError(null);
  };

  const renderLinkForm = (isOrg: boolean) => {
    const isEditing = isOrg ? editingOrgId : editingMyId;
    const handleSubmit = isOrg ? handleSubmitOrg : handleSubmitMy;

    return (
      <div className="mb-6 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-4">
          {isEditing ? 'Edit Link' : `Add New ${isOrg ? 'Organization' : 'Personal'} Link`}
        </h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., EDC Portal"
                className={`w-full ${fieldCls}`}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                URL <span className="text-red-500">*</span>
              </label>
              <input
                type="url"
                value={formData.url}
                onChange={e => setFormData(prev => ({ ...prev, url: e.target.value }))}
                placeholder="https://..."
                className={`w-full ${fieldCls}`}
                required
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description
            </label>
            <input
              type="text"
              value={formData.description}
              onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Brief description (optional)"
              className={`w-full ${fieldCls}`}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Icon URL
            </label>
            <input
              type="url"
              value={formData.icon_url}
              onChange={e => setFormData(prev => ({ ...prev, icon_url: e.target.value }))}
              placeholder="https://... (favicon or icon image)"
              className={`w-full ${fieldCls}`}
            />
          </div>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={handleCancelForm}
              className={secondaryBtnCls}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !formData.name || !formData.url}
              className={primaryBtnCls}
            >
              {saving ? 'Saving...' : isEditing ? 'Save Changes' : 'Add Link'}
            </button>
          </div>
        </form>
      </div>
    );
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <button
            onClick={() => navigate('/siteorganizer/studies')}
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200 mb-4"
          >
            <ArrowLeft size={16} />
            Back to Studies
          </button>
          <div>
            <h1 className="text-lg font-semibold leading-6 text-gray-900 dark:text-gray-100">Links</h1>
            <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">Manage organization and personal quick access links</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Site selector */}
          {sites.length > 1 && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Select Site
              </label>
              <select
                value={selectedSiteId}
                onChange={e => setSelectedSiteId(e.target.value)}
                className={`w-full max-w-xs ${fieldCls}`}
              >
                <option value="">Choose a site...</option>
                {sites.map(site => (
                  <option key={site.id} value={site.id}>
                    {site.name} ({site.code})
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 dark:bg-red-900/20 dark:border-red-800 rounded-lg text-sm text-red-600 dark:text-red-400">
              {error}
            </div>
          )}

          {!selectedSiteId ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              Select a site to manage links
            </div>
          ) : loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="bg-gray-100 dark:bg-gray-800 rounded-lg h-16 animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="space-y-8">
              {/* My Links Section */}
              <section>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <User size={20} className="text-primary-600 dark:text-primary-400" />
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">My Links</h2>
                    <span className="text-sm text-gray-500 dark:text-gray-400">({myLinks.length})</span>
                  </div>
                  <button
                    onClick={handleAddMy}
                    className={primaryBtnCls}
                  >
                    <Plus size={14} />
                    Add
                  </button>
                </div>

                {showMyForm && renderLinkForm(false)}

                {myLinks.length === 0 && !showMyForm ? (
                  <div className="text-center py-8 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-dashed border-gray-300 dark:border-gray-600">
                    <User size={32} className="mx-auto mb-2 text-gray-300 dark:text-gray-600" />
                    <p className="text-gray-500 dark:text-gray-400 text-sm">No personal links yet</p>
                    <button
                      onClick={handleAddMy}
                      className="mt-2 text-sm text-primary-600 dark:text-primary-400 hover:underline"
                    >
                      Add your first link
                    </button>
                  </div>
                ) : myLinks.length > 0 && (
                  <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 divide-y divide-gray-200 dark:divide-gray-700">
                    {myLinks.map(link => (
                      <div key={link.id} className="flex items-center gap-4 p-4">
                        <div className="flex-shrink-0 w-8 h-8 bg-primary-50 dark:bg-primary-900/20 rounded flex items-center justify-center">
                          {link.icon_url ? (
                            <img src={link.icon_url} alt="" className="w-5 h-5" />
                          ) : (
                            <ExternalLink size={16} className="text-primary-400" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <span className="font-medium text-gray-900 dark:text-gray-100">{link.name}</span>
                          <a
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-primary-600 dark:text-primary-400 hover:underline truncate block"
                          >
                            {link.url}
                          </a>
                          {link.description && (
                            <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{link.description}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleEditMy(link)}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:text-gray-500 dark:hover:text-gray-300 dark:hover:bg-gray-700 rounded-lg transition-colors duration-150"
                          >
                            <Pencil size={16} />
                          </button>
                          <button
                            onClick={() => handleDeleteMy(link.id)}
                            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:text-gray-500 dark:hover:text-red-400 dark:hover:bg-red-900/20 rounded-lg transition-colors duration-150"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              {/* Organization Links Section */}
              <section>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Building2 size={20} className="text-gray-600 dark:text-gray-400" />
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Organization Links</h2>
                    <span className="text-sm text-gray-500 dark:text-gray-400">({orgLinks.filter(l => l.is_active).length})</span>
                  </div>
                  {isAdmin && (
                    <button
                      onClick={handleAddOrg}
                      className={secondaryBtnCls}
                    >
                      <Plus size={14} />
                      Add
                    </button>
                  )}
                </div>

                {showOrgForm && renderLinkForm(true)}

                {orgLinks.length === 0 && !showOrgForm ? (
                  <div className="text-center py-8 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-dashed border-gray-300 dark:border-gray-600">
                    <Building2 size={32} className="mx-auto mb-2 text-gray-300 dark:text-gray-600" />
                    <p className="text-gray-500 dark:text-gray-400 text-sm">No organization links configured</p>
                    {isAdmin && (
                      <button
                        onClick={handleAddOrg}
                        className="mt-2 text-sm text-primary-600 dark:text-primary-400 hover:underline"
                      >
                        Add the first link
                      </button>
                    )}
                  </div>
                ) : orgLinks.length > 0 && (
                  <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 divide-y divide-gray-200 dark:divide-gray-700">
                    {orgLinks.map(link => (
                      <div
                        key={link.id}
                        className={`flex items-center gap-4 p-4 ${!link.is_active ? 'opacity-50' : ''}`}
                      >
                        <div className="flex-shrink-0 w-8 h-8 bg-gray-100 dark:bg-gray-700 rounded flex items-center justify-center">
                          {link.icon_url ? (
                            <img src={link.icon_url} alt="" className="w-5 h-5" />
                          ) : (
                            <ExternalLink size={16} className="text-gray-400 dark:text-gray-500" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900 dark:text-gray-100">{link.name}</span>
                            {!link.is_active && (
                              <span className="text-xs bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300 px-2 py-0.5 rounded">
                                Inactive
                              </span>
                            )}
                          </div>
                          <a
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-primary-600 dark:text-primary-400 hover:underline truncate block"
                          >
                            {link.url}
                          </a>
                          {link.description && (
                            <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{link.description}</p>
                          )}
                        </div>
                        {isAdmin && (
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleToggleActive(link)}
                              className={`px-3 py-1 text-xs font-medium rounded-lg transition-colors duration-150 ${
                                link.is_active
                                  ? 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                                  : 'bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/40 dark:text-green-300 dark:hover:bg-green-900/60'
                              }`}
                            >
                              {link.is_active ? 'Disable' : 'Enable'}
                            </button>
                            <button
                              onClick={() => handleEditOrg(link)}
                              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:text-gray-500 dark:hover:text-gray-300 dark:hover:bg-gray-700 rounded-lg transition-colors duration-150"
                            >
                              <Pencil size={16} />
                            </button>
                            <button
                              onClick={() => handleDeleteOrg(link.id)}
                              className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:text-gray-500 dark:hover:text-red-400 dark:hover:bg-red-900/20 rounded-lg transition-colors duration-150"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {!isAdmin && orgLinks.length > 0 && (
                  <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
                    Contact an administrator to add or modify organization links.
                  </p>
                )}
              </section>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default OrgLinks;
